import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Package, CheckCircle } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { INITIAL_RC_DIM } from '../../utils/rechartsDefaults';

const getComplexityColor = (complexity) => {
  switch (complexity) {
    case 'high': return 'bg-red-500';
    case 'medium': return 'bg-yellow-500';
    default: return 'bg-green-500';
  }
};

const ComplexityDepsPanel = ({ code_complexity, complexityDistribution, dependency_health }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <Card className="bg-gray-900 border-gray-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-white flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-500" />
          Complex Functions (Backend)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {complexityDistribution.length > 0 ? (
          <div className="flex gap-4">
            <div className="w-32 h-32 shrink-0">
              <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_RC_DIM}>
                <PieChart>
                  <Pie data={complexityDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={55} innerRadius={30}>
                    {complexityDistribution.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1.5 max-h-48 overflow-y-auto">
              {(code_complexity || []).map(entry => (
                entry.complex_functions.map(fn => (
                  <div key={`${entry.file}-${fn.name}`} className="flex items-center justify-between text-xs bg-gray-800 rounded px-2 py-1.5">
                    <div className="truncate mr-2">
                      <span className="text-gray-400">{entry.file.replace('.py', '')}.</span>
                      <span className="text-gray-200 font-medium">{fn.name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-gray-500">{fn.length}L</span>
                      <Badge className={`${getComplexityColor(fn.complexity)} text-xs px-1.5`}>{fn.complexity}</Badge>
                    </div>
                  </div>
                ))
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-gray-400">No high-complexity functions found</p>
          </div>
        )}
      </CardContent>
    </Card>

    <Card className="bg-gray-900 border-gray-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Package className="w-4 h-4 text-purple-500" />
          Outdated Dependencies
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-72 overflow-y-auto">
          {(dependency_health?.frontend?.outdated || []).length > 0 && (
            <div>
              <p className="text-xs font-medium text-emerald-400 mb-1.5 uppercase tracking-wide">Frontend ({dependency_health.frontend.total} total)</p>
              {dependency_health.frontend.outdated.slice(0, 8).map(dep => (
                <div key={dep.package} className="flex items-center justify-between text-xs bg-gray-800 rounded px-2 py-1.5 mb-1">
                  <span className="text-gray-200 font-medium truncate mr-2">{dep.package}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-red-400">{dep.current}</span>
                    <span className="text-gray-600">&rarr;</span>
                    <span className="text-green-400">{dep.latest}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {(dependency_health?.backend?.outdated || []).length > 0 && (
            <div>
              <p className="text-xs font-medium text-blue-400 mb-1.5 uppercase tracking-wide">Backend ({dependency_health.backend.total} total)</p>
              {dependency_health.backend.outdated.slice(0, 8).map(dep => (
                <div key={dep.package} className="flex items-center justify-between text-xs bg-gray-800 rounded px-2 py-1.5 mb-1">
                  <span className="text-gray-200 font-medium truncate mr-2">{dep.package}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-red-400">{dep.current}</span>
                    <span className="text-gray-600">&rarr;</span>
                    <span className="text-green-400">{dep.latest}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {(dependency_health?.frontend?.outdated || []).length === 0 && (dependency_health?.backend?.outdated || []).length === 0 && (
            <div className="text-center py-8">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-gray-400">All dependencies up to date</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  </div>
);

export default ComplexityDepsPanel;
